import { useEffect, useMemo, useState } from "react"
import ModalCustom from "components/ModalCustom"
import ActionFooterModalCard from "components/ModalCustom/ActionFooterModalCard"
import LabelNotificationPage from "components/Notification/LabelNotificationPage"
import { toast } from "react-toastify"
import { GroupsOrganizationRoleResponse } from "../groups.type"
import { postChangeRoleMemberGroupsOrganizationMiddleware } from "../groups.api"

interface Props {
  isOpen: boolean
  onClose: () => void
  onSuccess: (memberId: string, newRole: GroupsOrganizationRoleResponse) => void
  groupId: string
  memberId: string
  currentRoleId: string
  roles: GroupsOrganizationRoleResponse[]
}

export const ChangeGroupRoleModal = (props: Props) => {
  const {
    isOpen,
    onClose,
    onSuccess,
    groupId,
    memberId,
    currentRoleId,
    roles,
  } = props
  const [selectedRole, setSelectedRole] = useState<string>("")

  useEffect(() => {
    if (!isOpen) {
      return
    }
    setSelectedRole(currentRoleId)
  }, [isOpen, currentRoleId])

  const roleOptions = useMemo(() => roles || [], [roles])

  const handleChangeRole = (roleId: string) => () => {
    setSelectedRole((prev) => (prev === roleId ? "" : roleId))
  }

  const handleSubmit = async () => {
    try {
      await postChangeRoleMemberGroupsOrganizationMiddleware({
        group_organization_id: groupId,
        user_id: memberId,
        role_id: selectedRole,
      })
      toast(
        <LabelNotificationPage
          messenger="Update member role successfully!"
          type="success"
        />
      )
      const newRole = roles.find((role) => role.id === selectedRole)
      if (newRole) {
        onSuccess(memberId, newRole)
      }
    } catch (error: any) {
      toast(
        <LabelNotificationPage
          messenger={
            error?.response?.data?.message || "Update member role failed!"
          }
          type="error"
        />
      )
    }
  }

  if (!isOpen) {
    return null
  }

  const renderBody = () => {
    return (
      <div className="flex flex-col">
        <div className="grid grid-cols-2 gap-2">
          {roleOptions.map((role) => {
            const isActive = role.id === selectedRole
            return (
              <div
                key={role.id}
                className="rounded px-1 cursor-pointer min-h-[32px] flex items-center"
                style={{
                  border: isActive ? "1px solid #F7AC1B" : "1px solid #E4E4E4",
                  boxShadow: isActive
                    ? "0px 0px 0px 4px rgba(247, 172, 27, 0.1)"
                    : "none",
                  backgroundColor: isActive ? "#FFFFFF" : "#F7F7F7",
                }}
                onClick={handleChangeRole(role.id)}
              >
                <p
                  className="font-normal flex-1 text-center"
                  style={{
                    color: isActive ? "#222222" : "#7A7A7A",
                    fontSize: 14,
                    lineHeight: "24px",
                  }}
                >
                  {role.name}
                </p>
              </div>
            )
          })}
        </div>

        <ActionFooterModalCard
          onCloseModal={onClose}
          onSubmit={handleSubmit}
          handleDisableButton={
            !selectedRole || selectedRole === currentRoleId || !memberId
          }
          titleButton="Yes, change role"
          styleRoot={{
            marginTop: 20,
          }}
        />
      </div>
    )
  }

  return (
    <ModalCustom
      handleChangeButton={() => onClose}
      label="Change role"
      widthModal={490}
      bodyChildren={renderBody()}
    />
  )
}
